<template>
    <main class="publish-page">
        <div v-if="loadError" class="publish-error">
            <a-icon type="exclamation-circle"/>
            <strong>发布内容加载失败</strong>
            <span>{{ loadError }}</span>
        </div>
        <a-spin v-else :spinning="loading" class="publish-loading">
            <div class="publish-content">
                <section class="content-section copy-section">
                    <div class="copy-block">
                        <div class="section-label">标题</div>
                        <strong class="publish-copy">{{ publishContent.title || '暂无标题' }}</strong>
                    </div>
                    <!--                    <div v-if="isDouyin" class="copy-block content-copy-block">-->
                    <!--                        <div class="section-label">内容</div>-->
                    <!--                        <p class="publish-copy">{{ publishContent.content || '暂无内容' }}</p>-->
                    <!--                    </div>-->
                </section>


                <section class="content-section video-section">
                    <div class="section-label">视频</div>
                    <video
                            v-if="publishContent.videoUrl"
                            :src="publishContent.videoUrl"
                            controls
                            playsinline
                            preload="metadata"
                    />
                    <div v-else class="video-empty">
                        <a-icon type="video-camera"/>
                        <span>暂无可发布视频</span>
                    </div>
                </section>

                <section v-if="isDouyin" class="content-section">
                    <div class="section-label">标签</div>
                    <div v-if="publishHashtags.length" class="hashtag-list">
                        <span v-for="hashtag in publishHashtags" :key="hashtag">
                            #{{ hashtag }}
                        </span>
                    </div>
                    <p v-else class="empty-copy">暂无标签</p>
                </section>


            </div>
        </a-spin>

        <footer class="publish-actions">
            <button type="button" class="action-button back-button" @click="goBack">
                返回
            </button>
            <button
                    type="button"
                    class="action-button open-button"
                    :class="{wechat: isWechat}"
                    :disabled="loading || publishing || Boolean(loadError)"
                    @click="openTargetApp"
            >
                {{ publishing ? '正在准备投稿…' : openButtonText }}
            </button>
        </footer>

    </main>
</template>

<script>
import {Toast} from 'vant';
import 'vant/lib/toast/style';
import {
    createDouyinPublishSchema,
    createWechatMiniProgramScheme,
    drawShareContent
} from '../../api/h5Page';

const PUBLISH_STORAGE_KEY = 'ai-video-publish-content';

export default {
    name: 'PublishPage',
    data() {
        return {
            loading: false,
            publishing: false,
            douyinSchema: '',
            douyinSchemaExpiresAt: 0,
            douyinSchemaRefreshTimer: null,
            loadError: '',
            publishContent: {
                id: '',
                title: '',
                content: '',
                merchantTags: '',
                hashtags: '',
                videoUrl: '',
                itemType: '',
                targetUrl: '',
                plateNo: ''
            }
        };
    },
    created() {
        this.loadPublishContent();
    },
    beforeDestroy() {
        this.clearDouyinSchemaRefreshTimer();
    },
    computed: {
        itemType() {
            return this.publishContent.itemType
                || String(this.$route.query.type || '').trim();
        },
        isWechat() {
            return this.itemType === 'WECHAT_CHANNEL_PUBLISH';
        },
        isDouyin() {
            return this.itemType === 'DOUYIN_PUBLISH';
        },
        openButtonText() {
            return this.isWechat ? '打开微信' : '打开抖音';
        },
        publishHashtags() {
            const tags = [
                this.publishContent.merchantTags,
                this.publishContent.hashtags
            ].reduce((result, value) => (
                result.concat(this.parseHashtagSource(value)
                    .split(/[\s,，;；#＃]+/)
                    .map(item => item.trim().replace(/^[\[\]'"\s]+|[\[\]'"\s]+$/g, ''))
                    .filter(Boolean))
            ), []);
            return [...new Set(tags)];
        }
    },
    methods: {
        parseHashtagSource(value) {
            if (Array.isArray(value)) return value.join(' ');
            const text = String(value || '').trim();
            if (!text) return '';
            try {
                const parsed = JSON.parse(text);
                if (Array.isArray(parsed)) return parsed.join(' ');
                if (typeof parsed === 'string') return parsed;
            } catch (error) {
                // 兼容历史非 JSON 标签字符串，继续按分隔符解析。
            }
            return text;
        },
        async loadPublishContent() {
            const plateNo = String(this.$route.query.sn || '').trim();
            const itemType = String(this.$route.query.type || '').trim();
            try {
                const cached = JSON.parse(
                    window.sessionStorage.getItem(PUBLISH_STORAGE_KEY) || 'null'
                );
                if (cached
                    && cached.plateNo === plateNo
                    && cached.itemType === itemType
                    && Object.prototype.hasOwnProperty.call(cached, 'merchantTags')
                    && Object.prototype.hasOwnProperty.call(cached, 'hashtags')) {
                    this.publishContent = cached;
                    if (this.isDouyin) await this.prepareDouyinSchema();
                    return;
                }
            } catch (error) {
                window.sessionStorage.removeItem(PUBLISH_STORAGE_KEY);
            }

            if (!plateNo || !itemType) {
                this.loadError = '缺少发布页面参数';
                return;
            }

            this.loading = true;
            try {
                const shareContent = await drawShareContent(plateNo);
                this.publishContent = {
                    id: shareContent.id || '',
                    title: shareContent.title || '',
                    content: shareContent.content || '',
                    merchantTags: shareContent.merchantTags || '',
                    hashtags: shareContent.hashtags || '',
                    videoUrl: shareContent.videoUrl || '',
                    itemType,
                    targetUrl: '',
                    plateNo
                };
                window.sessionStorage.setItem(
                    PUBLISH_STORAGE_KEY,
                    JSON.stringify(this.publishContent)
                );
                if (this.isDouyin) await this.prepareDouyinSchema();
            } catch (error) {
                this.loadError = error.message || '无法获取发布内容';
            } finally {
                this.loading = false;
            }
        },
        goBack() {
            const plateNo = this.publishContent.plateNo
                || String(this.$route.query.sn || '').trim();
            if (window.history.length > 1) {
                this.$router.back();
                return;
            }
            this.$router.replace({path: '/app', query: {sn: plateNo}});
        },
        async openTargetApp() {
            if (this.isDouyin) {
                this.openDouyinPublish();
                return;
            }
            if (this.isWechat) {
                const plateNo = this.publishContent.plateNo
                    || String(this.$route.query.sn || '').trim();
                this.publishing = true;
                try {
                    const result = await createWechatMiniProgramScheme(plateNo, 'channels');
                    if (!result || !result.openlink) throw new Error('server 未返回小程序入口');
                    window.location.href = result.openlink;
                } catch (error) {
                    Toast.fail(error.message || '微信小程序打开失败');
                } finally {
                    this.publishing = false;
                }
                return;
            }
            Toast.fail('暂时无法打开对应应用');
        },
        async prepareDouyinSchema() {
            const plateNo = this.publishContent.plateNo
                || String(this.$route.query.sn || '').trim();
            if (!plateNo || !this.publishContent.id) {
                return;
            }
            this.publishing = true;
            try {
                const result = await createDouyinPublishSchema(
                    plateNo,
                    this.publishContent.id
                );
                if (!result || !result.schema) {
                    throw new Error('投稿参数生成失败');
                }
                this.douyinSchema = result.schema;
                this.douyinSchemaExpiresAt = Date.parse(result.expiresAt || '') || (Date.now() + 240000);
                this.scheduleDouyinSchemaRefresh();
            } catch (error) {
                this.douyinSchema = '';
                this.douyinSchemaExpiresAt = 0;
                this.clearDouyinSchemaRefreshTimer();
                Toast.fail(error.message || '投稿参数准备失败');
            } finally {
                this.publishing = false;
            }
        },
        openDouyinPublish() {
            if (!/Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '')) {
                Toast.fail('请在安装了抖音的手机浏览器中打开');
                return;
            }
            if (!this.douyinSchema || Date.now() >= this.douyinSchemaExpiresAt) {
                Toast('正在刷新投稿参数，请稍后再点一次');
                this.prepareDouyinSchema();
                return;
            }
            if (this.isRestrictedInAppBrowser()) {
                // 在微信内进入独立引导页，系统浏览器打开后直接继续投稿协议。
                this.$router.push({
                    path: '/app/browser-open-guide',
                    query: {platform: '抖音', target: this.douyinSchema}
                });
                return;
            }
            // Schema 已提前准备，本次用户点击直接同步触发自定义协议。
            window.location.href = this.douyinSchema;
        },
        scheduleDouyinSchemaRefresh() {
            this.clearDouyinSchemaRefreshTimer();
            const refreshDelay = Math.max(
                1000,
                this.douyinSchemaExpiresAt - Date.now() - 30000
            );
            this.douyinSchemaRefreshTimer = window.setTimeout(() => {
                this.prepareDouyinSchema();
            }, refreshDelay);
        },
        clearDouyinSchemaRefreshTimer() {
            if (this.douyinSchemaRefreshTimer) {
                window.clearTimeout(this.douyinSchemaRefreshTimer);
                this.douyinSchemaRefreshTimer = null;
            }
        },
        isRestrictedInAppBrowser() {
            return /MicroMessenger|QQ\/|Weibo|DingTalk|AlipayClient/i.test(
                navigator.userAgent || ''
            );
        }
    }
};
</script>

<style scoped>
.publish-page {
    position: relative;
    width: 100%;
    min-height: 100vh;
    min-height: 100dvh;
    padding-bottom: calc(88px + env(safe-area-inset-bottom));
    color: #1f2937;
    background: #f5f6f8;
}

.publish-loading,
.publish-loading /deep/ .ant-spin-container {
    min-height: calc(100vh - 88px);
    min-height: calc(100dvh - 88px);
}

.publish-content {
    padding: 18px 16px 28px;
}

.content-section {
    padding: 18px;
    border-radius: 12px;
    background: #fff;
}

.content-section + .content-section {
    margin-top: 12px;
}

.section-label {
    margin-bottom: 10px;
    color: #8b95a5;
    font-size: 12px;
}

.content-section h1 {
    margin: 0;
    color: #182230;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.65;
    word-break: break-word;
}

.copy-block + .copy-block {
    margin-top: 18px;
    padding-top: 18px;
    border-top: 1px solid #eef0f3;
}

.publish-copy,
.empty-copy {
    margin: 0;
    color: #354052;
    font-size: 14px;
    line-height: 1.75;
    white-space: pre-wrap;
    word-break: break-word;
}

.empty-copy {
    color: #a0a8b4;
}

.hashtag-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.hashtag-list span {
    padding: 5px 10px;
    border-radius: 999px;
    color: #2563eb;
    background: #eff6ff;
    font-size: 13px;
}

.video-section video {
    display: block;
    width: 100%;
    max-height: 58vh;
    border-radius: 10px;
    background: #000;
}

.video-empty {
    display: flex;
    min-height: 210px;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 10px;
    border-radius: 10px;
    color: #a0a8b4;
    background: #eef1f5;
}

.video-empty .anticon {
    font-size: 32px;
}

.publish-actions {
    position: fixed;
    z-index: 20;
    right: 0;
    bottom: 0;
    left: 0;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
    border-top: 1px solid #e7e9ed;
    background: rgba(255, 255, 255, .96);
    box-shadow: 0 -5px 20px rgba(31, 41, 55, .06);
    backdrop-filter: blur(12px);
}

.action-button {
    height: 48px;
    border: 0;
    border-radius: 10px;
    font-family: inherit;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
}

.back-button {
    color: #4b5563;
    background: #eef0f3;
}

.open-button {
    color: #fff;
    background: #111827;
}

.open-button.wechat {
    background: #07c160;
}

.open-button:disabled {
    opacity: .5;
    cursor: not-allowed;
}

.publish-error {
    display: flex;
    min-height: calc(100vh - 88px);
    min-height: calc(100dvh - 88px);
    align-items: center;
    justify-content: center;
    flex-direction: column;
    padding: 32px;
    color: #8b95a5;
    text-align: center;
}

.publish-error .anticon {
    color: #c3c9d1;
    font-size: 42px;
}

.publish-error strong {
    margin-top: 16px;
    color: #374151;
    font-size: 18px;
}

.publish-error span {
    margin-top: 8px;
    font-size: 13px;
}

@media (min-width: 520px) {
    .publish-page {
        width: 430px;
        margin: 0 auto;
        box-shadow: 0 0 50px rgba(25, 48, 83, .12);
    }

    .publish-actions {
        right: auto;
        left: 50%;
        width: 430px;
        transform: translateX(-50%);
    }
}
</style>
